import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const pipeline = [
  { label: '课堂视频', icon: 'ri-video-line', color: 'primary' as const },
  { label: '单课报告', icon: 'ri-file-list-3-line', color: 'accent' as const },
  { label: '教师档案', icon: 'ri-user-line', color: 'primary' as const },
  { label: '教研组统计', icon: 'ri-team-line', color: 'accent' as const },
  { label: '学校质量图', icon: 'ri-building-line', color: 'primary' as const },
  { label: '省域质量图', icon: 'ri-map-2-line', color: 'accent' as const },
];

export default function P75ModelComparison({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />数据管道
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            数据如何从<span className="text-primary-600">单课</span>进入<span className="text-gold-600">治理图</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            治理图不是重新填写出来的，而是从单课证据逐级汇总、经过口径校验形成的
          </p>
        </div>

        {/* Pipeline Flow */}
        <div className={`max-w-4xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex flex-col items-center gap-1">
            {pipeline.map((p, i) => (
              <div key={p.label} className="flex flex-col items-center w-full max-w-md">
                <div
                  className="w-full p-4 glass-liquid rounded-xl flex items-center gap-3"
                  style={{ transitionDelay: `${300 + i * 100}ms` }}
                >
                  <div className={`w-10 h-10 rounded-xl ${p.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${p.icon} text-base ${p.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                  </div>
                  <div className="flex-1">
                    <span className="text-sm font-body font-bold text-foreground-800">{p.label}</span>
                  </div>
                  <span className="text-xs font-body text-gold-600 font-semibold whitespace-nowrap">可回到下一级证据</span>
                </div>
                {i < pipeline.length - 1 && (
                  <div className="flex items-center gap-2 my-1">
                    <i className="ri-arrow-down-line text-lg text-foreground-300" />
                  </div>
                )}
                {/* Calibration node */}
                {i === pipeline.length - 2 && (
                  <div className="flex items-center gap-1 my-1">
                    <span className="px-3 py-1 rounded-full text-xs font-body font-bold glass-gold border border-gold-200/40 text-gold-700">
                      口径校验
                    </span>
                    <i className="ri-arrow-down-line text-lg text-foreground-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              每一个趋势、每一个颜色块、每一个管理判断，都能够<strong className="text-gold-700">向下追溯</strong>，必要时一直回到原始课堂片段。跨课程汇总时，只有定义一致、证据来源一致、统计方式一致的共同维度才进入分析。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}